import { useEffect, useState } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { TabGroup } from 'calc-nav';
import type { TabDef } from 'calc-nav';
import { FutureGradesTab } from './scenarioTabs/FutureGradesTab';
import { TargetGPATab } from './scenarioTabs/TargetGPATab';
import { CourseSwapTab } from './scenarioTabs/CourseSwapTab';
import { ScenarioResultTable } from './scenarioTabs/ScenarioResultTable';

export function ScenarioPanel() {
  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);
  const savedScenarios = useAppStore((s) => s.savedScenarios);
  const deleteScenario = useAppStore((s) => s.deleteScenario);
  const pendingScenarioTab = useAppStore((s) => s.pendingScenarioTab);
  const clearPendingScenarioTab = useAppStore((s) => s.clearPendingScenarioTab);

  // FE-E1 — when a "Set as target" bridge fires, remount the nested TabGroup
  // with the requested tab as its default. TabGroup only reads `defaultTab`
  // on mount, so bumping the key forces it to honor the new starting tab.
  const [tabMountKey, setTabMountKey] = useState(0);
  const [pendingDefault, setPendingDefault] = useState<string | null>(null);

  useEffect(() => {
    if (pendingScenarioTab) {
      setPendingDefault(pendingScenarioTab);
      setTabMountKey((k) => k + 1);
      clearPendingScenarioTab();
    }
  }, [pendingScenarioTab, clearPendingScenarioTab]);

  // Nested TabGroup — lives inside the main "scenarios" tab panel, so it must
  // NOT persist its own URL hash (a single hash is shared with the parent
  // group). section="scenarios" keeps ARIA/panel IDs unique.
  const scenarioTabs: TabDef[] = [
    { id: 'future', label: 'Future Grades', content: <FutureGradesTab courses={courses} settings={settings} data-testid="tab-future" /> },
    { id: 'target', label: 'Target GPA', content: <TargetGPATab courses={courses} settings={settings} data-testid="tab-target" /> },
    { id: 'swap', label: 'Course Swap', content: <CourseSwapTab courses={courses} settings={settings} data-testid="tab-swap" /> },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-surface p-4">
        <h3 className="mb-1 text-sm font-semibold text-[var(--color-text)]">Scenario Modeling</h3>
        <p className="mb-3 text-xs text-[var(--color-text-muted)]">
          Project how future grades, a course swap, or a target GPA would change every variant. Results are not saved to your transcript.
        </p>
        <TabGroup
          key={tabMountKey}
          section="scenarios"
          tabs={scenarioTabs}
          persistHash={false}
          {...(pendingDefault ? { defaultTab: pendingDefault } : {})}
        />
      </div>

      {/* R37 — Saved Scenarios */}
      {savedScenarios.length > 0 && (
        <div className="rounded-lg border border-border bg-surface p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--color-text)]">Saved Scenarios</h3>
            <span className="text-xs text-[var(--color-text-muted)]">{savedScenarios.length} saved</span>
          </div>
          <div className="space-y-3">
            {savedScenarios.map((saved) => (
              <div key={saved.id} className="rounded-lg border border-border bg-[var(--color-bg-secondary)] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-[var(--color-text)]">{saved.name}</span>
                  <button
                    onClick={() => deleteScenario(saved.id)}
                    className="rounded p-1 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-tertiary)]"
                    aria-label="Delete saved scenario"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <ScenarioResultTable result={saved.result} />
                <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                  {new Date(saved.createdAt).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
          {/* Side-by-side comparison when 2+ scenarios */}
          {savedScenarios.length >= 2 && (
            <div className="mt-4">
              <h4 className="mb-2 text-xs font-semibold text-[var(--color-text)]">Side-by-Side Comparison</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <caption className="sr-only">Saved scenarios comparison</caption>
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">Variant</th>
                      {savedScenarios.map((s) => (
                        <th key={s.id} scope="col" className="px-2 py-1.5 font-medium text-[var(--color-text-secondary)]">{s.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(savedScenarios[0].result.projectedGPA).map((label) => (
                      <tr key={label} className="border-b border-border">
                        <td className="px-2 py-1.5 text-[var(--color-text)]">{label}</td>
                        {savedScenarios.map((s) => (
                          <td key={s.id} className="px-2 py-1.5 text-[var(--color-text)] font-mono">
                            {s.result.projectedGPA[label]?.toFixed(2) ?? '—'}
                            <span className={`ml-1 ${(s.result.delta[label] ?? 0) >= 0 ? 'text-success' : 'text-error'}`}>
                              {(s.result.delta[label] ?? 0) >= 0 ? '↑' : '↓'}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
